import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import cmsClient from "src/services/cmsClient";
import { adaptStoreReport } from "src/features/reporting/adapters";
import StoreObservationTableCell from "../../components/StoreObservationTableCell/StoreObservationTableCell";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import { UseFormSetValue } from "react-hook-form";
import { checkObservationFormType } from "src/utils/checkObservationFormType";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

interface StoreReportOptions {
	year: number;
	form: number;
	areaId: number;
	compliancy?: boolean;
	threshold?: boolean;
	observationType: IObservationType;
	setValue: UseFormSetValue<ReportDetailForm>;
}

const columnHelper = createColumnHelper<any>();

const downloadStoreReport = async (
	year: number,
	form: number,
	area: number,
	type: "complete" | "compliant" | "threshold"
) => {
	const response = await cmsClient.get<any>(
		"/observations/reports/store-by-month/csv/",
		{ params: { year, form, area, type, includeIgnored: true } }
	);

	return response.data;
};

const fetchStoreReport = async (year: number, form: number, area: number) => {
	const response = await cmsClient.get<IStoreReport[]>(
		"/observations/reports/store-by-month/",
		{
			params: {
				year,
				form,
				area,
				includeIgnored: true
			}
		}
	);

	return response.data;
};

const useStoreReport = ({
	year,
	form,
	areaId,
	compliancy,
	threshold,
	observationType,
	setValue
}: StoreReportOptions) => {
	const queryKey = ["store-report", { year, form, areaId }];
	const queryFn = () => fetchStoreReport(year, form, areaId);
	const select = (list: IStoreReport[]) => adaptStoreReport(list);

	const query = useQuery({ queryKey, queryFn, select });

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "INDIVIDUAL") {
					return <IndividualObservationTableCell cellValue={cell.getValue()} />;
				}

				if (observationType === "GREAT_CONVERSATION") {
					return <GreatConversationTableCell cellValue={cell.getValue()} />;
				}

				return (
					<StoreObservationTableCell
						cellValue={cell.getValue()}
						showStatistics={false}
						storeId={row.original.storeId}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "store-name",
			cell: ({ row }) => row.original.storeName,
			header: "Store Name"
		}),
		...monthlyColumns
	];

	const download = async () => {
		const response = await downloadStoreReport(
			year,
			form,
			areaId,
			compliancy ? "compliant" : threshold ? "threshold" : "complete"
		);

		const url = window.URL.createObjectURL(new Blob([response]));

		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "store-by-month.csv");

		document.body.appendChild(link);

		link.click();
	};

	return { columns, download, ...query };
};

export default useStoreReport;
